/**
 * The quick mission log — a mission a manager records for a driver in two taps,
 * without opening the task form.
 *
 * The board (`./task`) is where a *dispatched* mission lives: it carries a type,
 * a date, a contact, a destination and a convoy, and it exists so the unit can
 * run it. A great deal of what a driver actually does never gets that far —
 * "נסיעה לקצין עיר", a run to the depot, a lift somebody asked for on the way
 * out. Until now those journeys were invisible, which meant the one question a
 * מ"פ asks before handing out the next job — *who has done the least today* —
 * was answered off half the evidence.
 *
 * So a quick task is deliberately the smallest record the question needs: a
 * driver, a sentence, and who wrote it down. It is not a task. It has no status,
 * no lifecycle, no crew and no vehicle, and nothing in the application dispatches
 * one — the two are only ever added together, by
 * `src/core/driver-workload.ts`, into the workload a manager balances against.
 *
 * The driver's name is frozen onto the row beside their id, exactly as the audit
 * ledger freezes an actor's: the graph has to stay legible on a device that
 * pulled the row from the cloud before it pulled the roster.
 */
import type { AuditedEntity } from './index';

/**
 * The longest a description may be. A quick log is a line on a bar chart's
 * tooltip, not a report — anything longer is a task and belongs on the board.
 */
export const QUICK_TASK_DESCRIPTION_MAX = 120;

/**
 * One journey somebody logged by hand.
 *
 * `createdByName` is required here, unlike on {@link AuditedEntity}: the audit
 * trail is the whole point of the record, and a row that cannot say who filed it
 * is worse than no row at all.
 */
export interface QuickDriverTask extends AuditedEntity {
  /** The `Soldier.id` the mission is credited to. */
  driverId: string;
  /** The driver's name as it stood when the mission was logged. */
  driverName: string;
  /** What they did — "נסיעה לקצין עיר". */
  description: string;
  /** The `User.id` of the operator who filed it. */
  createdBy: string;
  /** The operator's name, frozen. Never rewritten. */
  createdByName: string;
}

/** What the quick form hands the store — ids and stamps are the store's job. */
export interface QuickDriverTaskDraft {
  driverId: string;
  description: string;
}
